import React from 'react';

export const HERO_IMAGE = 'linear-gradient(135deg, #fbcfe8 0%, #f9a8d4 40%, #fde68a 100%)';

/* Product collage (Instagram bundle style) — transparent background, no outer
   frame. Five templates; layout adapts to the product count. Used by the
   checkout-link editor preview AND by the social calendar Library so the
   bundle tile matches what was saved on the link. */
export function ProductCollage({
    products,
    height = 200,
    padding = 12,
    radius = 12,
    template = 'bundle',
    fallback = HERO_IMAGE,
}) {
    const all = Array.isArray(products) ? products.filter((p) => p?.imageUrl) : [];

    if (all.length === 0) {
        return <div style={{ height, background: fallback, borderRadius: radius }} />;
    }

    const tile = (p, style, badge) => (
        <div
            key={p.id ?? p.imageUrl}
            style={{
                borderRadius: radius,
                background: `url("${p.imageUrl}") center/cover no-repeat #f5f5f4`,
                boxShadow: '0 6px 16px rgba(0,0,0,0.10)',
                position: 'relative',
                ...style,
            }}
        >
            {badge}
        </div>
    );

    const moreBadge = (n) => (
        <div style={{
            position: 'absolute', inset: 0, borderRadius: radius,
            background: 'rgba(28,25,23,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 22, fontWeight: 600, letterSpacing: 0.3,
        }}>+{n}</div>
    );

    if (template === 'stacked') {
        const items = all.slice(0, 4);
        const size = Math.min(height - padding * 2 - 24, 150);
        const offsets = [
            { rotate: 0, x: 0, y: 0 },
            { rotate: -11, x: -52, y: -2 },
            { rotate: 11, x: 50, y: 2 },
            { rotate: -5, x: -10, y: 22 },
        ];
        return (
            <div style={{ height, padding, background: 'transparent', position: 'relative' }}>
                {items.slice().reverse().map((p, idx) => {
                    const i = items.length - 1 - idx;
                    const lay = offsets[i] || offsets[offsets.length - 1];
                    return (
                        <div key={p.id ?? i} style={{
                            position: 'absolute',
                            width: size, height: size,
                            left: `calc(50% + ${lay.x}px - ${size / 2}px)`,
                            top: `calc(50% + ${lay.y}px - ${size / 2}px)`,
                            transform: `rotate(${lay.rotate}deg)`,
                            borderRadius: radius,
                            background: `url("${p.imageUrl}") center/cover no-repeat #f5f5f4`,
                            boxShadow: i === 0
                                ? '0 14px 30px rgba(0,0,0,0.18)'
                                : '0 6px 14px rgba(0,0,0,0.10)',
                            border: '4px solid #fff',
                            zIndex: items.length - i,
                        }} />
                    );
                })}
            </div>
        );
    }

    if (template === 'polaroid') {
        const items = all.slice(0, 4);
        const tileH = (height - padding * 2) * 0.85;
        const tileW = tileH * 0.78;
        const rotations = [-8, 5, -4, 9];
        return (
            <div style={{
                height, padding,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent',
            }}>
                {items.map((p, i) => (
                    <div key={p.id ?? i} style={{
                        width: tileW, height: tileH,
                        background: '#fff',
                        padding: 5, paddingBottom: 14,
                        boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
                        marginLeft: i === 0 ? 0 : -tileW * 0.2,
                        transform: `rotate(${rotations[i] || 0}deg)`,
                        zIndex: items.length - i,
                    }}>
                        <div style={{
                            width: '100%', height: '100%',
                            background: `url("${p.imageUrl}") center/cover no-repeat #f5f5f4`,
                        }} />
                    </div>
                ))}
            </div>
        );
    }

    if (template === 'carousel') {
        const items = all.slice(0, 5);
        const center = Math.floor(items.length / 2);
        return (
            <div style={{
                height, padding,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent',
            }}>
                {items.map((p, i) => {
                    const dist = Math.abs(i - center);
                    const isCenter = dist === 0;
                    const tileH = (height - padding * 2) * (isCenter ? 1 : 0.78);
                    const tileW = tileH * 0.78;
                    return (
                        <div key={p.id ?? i} style={{
                            width: tileW, height: tileH,
                            borderRadius: radius,
                            background: `url("${p.imageUrl}") center/cover no-repeat #f5f5f4`,
                            boxShadow: `0 6px 16px rgba(0,0,0,${0.14 - dist * 0.03})`,
                            marginLeft: i === 0 ? 0 : -tileW * 0.22,
                            zIndex: items.length - dist,
                            border: '3px solid #fff',
                            opacity: 1 - dist * 0.08,
                        }} />
                    );
                })}
            </div>
        );
    }

    if (template === 'mosaic') {
        const items = all.slice(0, 5);
        const extra = all.length - items.length;
        if (items.length === 1) {
            return (
                <div style={{
                    height, padding, background: 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {tile(items[0], { width: Math.round((height - padding * 2) * 0.95), height: height - padding * 2 })}
                </div>
            );
        }
        return (
            <div style={{ height, padding, gap: 8, display: 'flex', background: 'transparent' }}>
                {tile(items[0], { flex: 1.6 })}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {items.slice(1).map((p, i, arr) =>
                        tile(p, { flex: 1 }, (i === arr.length - 1 && extra > 0) ? moreBadge(extra) : null)
                    )}
                </div>
            </div>
        );
    }

    // bundle (default) — adaptive grid
    const items = all.slice(0, 4);
    const extra = all.length - items.length;

    if (items.length === 1) {
        return (
            <div style={{
                height, padding, background: 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {tile(items[0], { width: Math.round((height - padding * 2) * 0.95), height: height - padding * 2 })}
            </div>
        );
    }

    if (items.length === 2) {
        return (
            <div style={{ height, padding, gap: 10, display: 'flex', background: 'transparent' }}>
                {items.map((p) => tile(p, { flex: 1 }))}
            </div>
        );
    }

    if (items.length === 3) {
        return (
            <div style={{ height, padding, gap: 10, display: 'flex', background: 'transparent' }}>
                {tile(items[0], { flex: 1.4 })}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {tile(items[1], { flex: 1 })}
                    {tile(items[2], { flex: 1 })}
                </div>
            </div>
        );
    }

    return (
        <div style={{
            height, padding, gap: 10,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            background: 'transparent',
        }}>
            {items.map((p, i) => tile(p, {}, (i === 3 && extra > 0) ? moreBadge(extra) : null))}
        </div>
    );
}

export default ProductCollage;
